import React, { useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Home, Users, Wallet as WalletIcon, User, Plus, MessageCircle, LayoutGrid, Flame, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LanguageContext";
import { useAppSub } from "@/context/AppSubContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { initials } from "@/lib/format";
import NotificationBell from "@/components/NotificationBell";

const AppShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { sub } = useAppSub();
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  // Sync preferred language from the user's profile (once, when it differs).
  useEffect(() => {
    if (user?.language && user.language !== lang) {
      setLang(user.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  const navItems = [
    { to: "/", label: t("nav.home"), icon: Home, testid: "nav-home" },
    { to: "/amis", label: t("nav.friends"), icon: Users, testid: "nav-friends" },
    { to: "/creer", label: "Créer", icon: Plus, center: true, testid: "nav-send" },
    { to: "/portefeuille", label: t("nav.wallet"), icon: WalletIcon, testid: "nav-wallet" },
    { to: "/profil", label: t("nav.profile"), icon: User, testid: "nav-profile" },
  ];

  return (
    <div className="min-h-screen pb-24">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-card/80 backdrop-blur border-b border-border">
        <div className="max-w-md lg:max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <span className="h-9 rounded-lg bg-[#05070d] px-1.5 grid place-items-center shadow-[0_10px_30px_-18px_rgba(15,23,42,0.35)]">
              <img src="/photopay-logo.jpeg" alt="MIHOD PAY" className="h-8 w-auto object-contain rounded" />
            </span>
          </Link>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate("/explorer")}
              className="h-9 w-9 grid place-items-center rounded-xl hover:bg-secondary transition-colors"
              data-testid="nav-explore"
              aria-label="Explorer"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/decouvrir")}
              className="h-9 w-9 grid place-items-center rounded-xl hover:bg-secondary transition-colors"
              data-testid="nav-feed"
              aria-label="Découvrir"
            >
              <Flame className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/mur")}
              className="h-9 w-9 grid place-items-center rounded-xl hover:bg-secondary transition-colors"
              data-testid="nav-wall"
              aria-label={t("nav.wall")}
            >
              <LayoutGrid className="h-5 w-5" />
            </button>
            <NotificationBell />
            <button
              onClick={() => navigate("/messages")}
              className="h-9 w-9 grid place-items-center rounded-xl hover:bg-secondary transition-colors"
              data-testid="nav-messages"
              aria-label="Messages"
            >
              <MessageCircle className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/profil")}
              className="ml-1"
              data-testid="topbar-avatar"
              aria-label="Profil"
            >
              <Avatar className="h-9 w-9">
                {user?.avatar && <AvatarImage src={user.avatar} alt={user.username} />}
                <AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">
                  {initials(user?.username)}
                </AvatarFallback>
              </Avatar>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-md lg:max-w-3xl mx-auto px-4 py-5">
        {sub?.status === "trial" && typeof sub?.days_left === "number" && sub.days_left <= 5 && (
          <button
            onClick={() => navigate("/abonnement")}
            className="w-full mb-4 rounded-xl border border-primary/40 bg-primary/10 px-4 py-2.5 text-sm text-left flex items-center justify-between gap-2"
            data-testid="trial-reminder-banner"
          >
            <span>
              {sub.days_left === 0
                ? "Votre essai gratuit se termine aujourd'hui."
                : `Essai gratuit : ${sub.days_left} jour(s) restant(s).`}{" "}
              <span className="text-primary font-medium">Choisir une formule →</span>
            </span>
          </button>
        )}
        {children}
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-card/90 backdrop-blur border-t border-border">
        <div className="max-w-md lg:max-w-3xl mx-auto px-2 h-16 grid grid-cols-5">
          {navItems.map((item) => {
            const active = isActive(item.to);
            const Icon = item.icon;
            if (item.center) {
              return (
                <button
                  key={item.to}
                  onClick={() => navigate(item.to)}
                  data-testid={item.testid}
                  className="flex items-center justify-center"
                  aria-label={item.label}
                >
                  <span className="h-12 w-12 -mt-6 rounded-2xl bg-primary text-primary-foreground grid place-items-center shadow-[0_0_22px_rgba(45,132,255,0.6),0_0_48px_rgba(45,132,255,0.28)] active:scale-95 transition-transform">
                    <Icon className="h-6 w-6" />
                  </span>
                </button>
              );
            }
            return (
              <button
                key={item.to}
                onClick={() => navigate(item.to)}
                data-testid={item.testid}
                aria-label={item.label}
                className={`flex flex-col items-center justify-center gap-0.5 text-[11px] transition-colors ${
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className={`h-5 w-5 ${active ? "fill-primary/10" : ""}`} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default AppShell;
